import { booleanAttribute, Component, ElementRef, inject, input, numberAttribute, OnInit, Optional, output, Renderer2, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { DmTableGrouppedRows, DmTableModule } from '@shared/_controls/dm-table';
import { DmTableController } from '@shared/_controls/dm-table';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';

import { LoadingComponent } from '@shared/_utils/loading.component';
import { CtrlSearchComponent } from '@shared/_controls/ctrl-search';
import { ChatRowComponent } from '@shared/_table-rows/chat';

import { BaseComponent } from '@base/base.component';
import { Chat, ChatFilter, ChatType, StateCacheMapsType, Tag, sortStringsBy } from '@models';
import { StoreService } from '@services/store.service';
import { FilterController } from '@shared/_controls/filter-form';
import { CacheService } from '@root/src/app/_services';
import { ErrorPanelComponent } from '@shared/_utils/error-panel';

@Component({
    selector: 'vz-chats-select, [vz-chats-select]',
    templateUrl: './chats-select.component.html',
    styleUrls: ['./chats-select.component.less'],
    imports: [
        CommonModule, FormsModule, ReactiveFormsModule,
        NzButtonModule, NzCheckboxModule,
        DmTableModule,
        LoadingComponent, CtrlSearchComponent, ChatRowComponent, ErrorPanelComponent
    ],
    host: {
        '(animate.leave)': 'animateClose($event)',
    }
})
@Tag('ChatsSelectComponent')
export class ChatsSelectComponent extends BaseComponent implements OnInit {

    readonly drawerEl       = viewChild('drawerEl', { read: ElementRef });
    readonly backdropEl     = viewChild('backdropEl', { read: ElementRef });

    readonly chatsFilter    = input<(c: Chat) => boolean>(x => !!x && x.orgId == this._orgId());
    readonly selected       = input<string[]>([]);
    readonly okText         = input<string>('Применить');
    readonly single         = input(false, { transform: booleanAttribute });
    readonly noFilters      = input(false, { transform: booleanAttribute });
    readonly avatarSize     = input(46, { transform: numberAttribute });
    readonly onClose        = output<Chat[] | undefined>();

    readonly controller: DmTableController<Chat, string> = new DmTableController((t: Chat) => t.id!, true);
    readonly filter = new ChatFilter(() => this.maps.orgs, () => this.userId);
    readonly filterController = new FilterController(this.filter, { route: this._route, router: this._router, qsKey: 'chatsFilter' });
    readonly maps: StateCacheMapsType = {};
    readonly cState         = toSignal(this.controller.state);

    private readonly _r2        = inject(Renderer2);
    private readonly _cache     = inject(CacheService);

    constructor(
        protected _store: StoreService,
        @Optional() private _drawerRef: NzDrawerRef<string[], Chat[] | string[] | undefined>,
        private _router: Router,
        private _route: ActivatedRoute,
    ) {
        super(_store);
        this._store.fillWithStates(['orgs', 'persons'], this.maps);
        this.controller.hiddenFilterFn = x => this.chatsFilter()(x);
        this.controller.filterFn = (item, filter) => this.filter.match(item, this.filter.parse(filter));
        this.controller.sortFn = items => {
            const chs = items.sort(sortStringsBy('title'));
            const mainChat = chs.find(ch => ch.isDefault);
            const favChat = chs.find(ch => ch.thread.commentators.length == 1 && ch.thread.commentators[0]?.id == this.userId && ch.type == ChatType.Direct);
            items = chs.filter(ch => (!mainChat || ch.id != mainChat.id) && (!favChat || ch.id != favChat.id));
            if (mainChat) {
                items.unshift(mainChat);
            }
            if (favChat) {
                items.unshift(favChat);
            }
            return items;
        };
        this._S.chats = this._cache.watchList('chat').subscribe(st => {
            this._loading.set(!!st.loading);
            if (st.err) {
                this._error.set(st.err);
            }
            else {
                const items = st.items || [];
                const gItems: DmTableGrouppedRows<Chat>[] = [
                    {
                        rows: items.filter(ch => ch.type != ChatType.Direct && !ch.pluginId),
                        data: { title: 'Групповые чаты', type: 1 }
                    },
                    {
                        rows: items.filter(ch => ch.type == ChatType.Direct && !ch.pluginId),
                        data: { title: 'Персональные чаты', type: 2 }
                    },
                    {
                        rows: items.filter(ch => !!ch.pluginId),
                        data: { title: 'Чаты с клиентами', type: 3 }
                    }
                ];
                this.controller.setItems(gItems);
                const selected = this.selected();
                if (selected?.length > 0) {
                    this.controller.setSelected(selected, true);
                }
            }
        });
    }

    // setItems(items: Chat[]): void {
    //     items.filter(ch => ch.type == ChatType.Direct && !ch.pluginId).forEach(ch => {
    //         let list = '';
    //         if(ch._activeMembers && ch._activeMembers.length == 1 && ch._activeMembers[0] == this.userId) {
    //             list = 'Избранное';
    //         }
    //         else if (ch._activeMembers?.length) {
    //             list = ch._activeMembers?.filter(id => id != this.userId).map(id => this.maps.persons![id].name || '#' + id).join(', ');
    //         }
    //         ch.title = list;
    //     });
    //     const gItems: DmTableGrouppedRows<Chat>[] = [
    //         {
    //             rows: items.filter(ch => ch.type != ChatType.Direct && !ch.pluginId),
    //             data: { title: 'Групповые чаты', type: 1 }
    //         },
    //         {
    //             rows: items.filter(ch => ch.type == ChatType.Direct && !ch.pluginId),
    //             data: { title: 'Персональные чаты', type: 2 }
    //         },
    //         {
    //             rows: items.filter(ch => !!ch.pluginId),
    //             data: { title: 'Чаты с клиентами', type: 3 }
    //         }
    //     ];
    //     this.controller.setItems(gItems);
    //     if (this.selected && this.selected.length > 0) {
    //         this.controller.setSelected(this.selected, true);
    //     }
    // }

    close(selected?: Chat[]): void {
        this._L('close', 'selected:', selected);
        if (this._drawerRef) {
            this._drawerRef.close(selected);
        }
        else {
            this.onClose.emit(selected);
        }
    }

    animateClose(e: any): void {
        const drawer = this.drawerEl()?.nativeElement;
        const backdrop = this.backdropEl()?.nativeElement;
        if (drawer && backdrop) {
            this._r2.addClass(drawer, 'vza-hide-rx');
            this._r2.addClass(backdrop, 'vza-hide-op');
            setTimeout(() => e.animationComplete(), 200);
        }
        else {
            e.animationComplete();
        }
    }

}
